import { useContext, useRef, useState } from "react";
import axios from "axios";
import { UserContext } from "../context/User";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const USAGE_OPTIONS = [
  { value: "house-of-worship", label: "House of Worship" },
  { value: "music-venue", label: "Music Venue" },
  { value: "permanent-install", label: "Permanent Install" },
  { value: "tour", label: "Tour" },
  { value: "rental-house", label: "Rental House" },
  { value: "theatre", label: "Theatre" },
];

// Maps each usage value to a marketing segment ID
const SEGMENT_MAP = {
  "permanent-install": 1,
  "music-venue": 2,
  tour: 2,
  "rental-house": 2,
  "house-of-worship": 4,
  theatre: 5,
};

const ProductRegistrationForm = ({ onSuccess }) => {
  const { user } = useContext(UserContext);
  const [serialNumber, setSerialNumber] = useState("");
  const [isNew, setIsNew] = useState(true);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [file, setFile] = useState(null);
  const [usageLocations, setUsageLocations] = useState([]);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const applyFile = (f) => {
    setFile(f);
    setErrors((prev) => ({ ...prev, receiptImage: "" }));
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setErrors((prev) => ({ ...prev, receiptImage: "" }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) {
      clearFile();
      return;
    }
    applyFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer?.files[0];
    if (!f) return;
    const dt = new DataTransfer();
    dt.items.add(f);
    if (fileInputRef.current) fileInputRef.current.files = dt.files;
    applyFile(f);
  };

  const handleDropZoneKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const toggleUsage = (value) => {
    setUsageLocations((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const validate = () => {
    const next = {};
    if (!serialNumber.trim()) {
      next.serialNumber = "Serial number is required.";
    }
    if (!purchaseDate) {
      next.purchaseDate = "Purchase date is required.";
    }
    if (!file) {
      next.receiptImage = "A receipt is required.";
    } else if (file.size > MAX_FILE_SIZE) {
      next.receiptImage = "File must be 10 MB or smaller.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    if (!validate()) return;

    setSubmitting(true);

    const products = JSON.stringify([
      { serialNumber: serialNumber.trim(), new: isNew },
    ]);
    const body = new FormData();
    body.append("purchaseDate", new Date(purchaseDate).toISOString());
    body.append("products", products);
    body.append("receipt", file);

    const authHeader = axios.defaults.headers.common["Authorization"];
    const headers = authHeader ? { Authorization: authHeader } : {};

    try {
      const regResponse = await fetch(
        "https://api.blizzardpro.com/registration",
        {
          method: "POST",
          headers,
          body,
        },
      );
      const regText = await regResponse.text();
      if (!regResponse.ok) {
        console.error("Registration error", regResponse.status, regText);
        throw new Error(`Server responded ${regResponse.status}`);
      }

      // Deduplicate segment IDs from selected usage locations
      const segmentIds = [
        ...new Set(
          usageLocations
            .filter((v) => SEGMENT_MAP[v])
            .map((v) => SEGMENT_MAP[v]),
        ),
      ];

      const marketingPayload = {
        email: user?.email,
        segments: segmentIds.map((id) => ({ id })),
      };

      const mktResponse = await fetch(
        "https://api.blizzardpro.com/marketing-contact",
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(marketingPayload),
        },
      );
      if (!mktResponse.ok) {
        console.error("Marketing contact error", mktResponse.status);
      }

      onSuccess?.();
      setSerialNumber("");
      setIsNew(true);
      setPurchaseDate("");
      clearFile();
      setUsageLocations([]);
      setEmailOptIn(false);
    } catch (err) {
      console.error("Submission error", err);
      setStatus("Sorry, something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <fieldset disabled={submitting} className="border-0 p-0 m-0">
        {/* Serial Number */}
        <div className="mb-4">
          <label
            htmlFor="serialNumber"
            className="block text-base font-medium text-gray-700 mb-1"
          >
            Serial Number{" "}
            <span className="text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="serialNumber"
            name="serialNumber"
            type="text"
            placeholder="Enter serial number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            autoComplete="off"
            className={`w-full border rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.serialNumber ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.serialNumber && (
            <p className="text-red-500 text-sm mt-1" role="alert">
              {errors.serialNumber}
            </p>
          )}
        </div>

        {/* Purchase Date */}
        <div className="mb-4">
          <label
            htmlFor="purchaseDate"
            className="block text-base font-medium text-gray-700 mb-1"
          >
            Purchase Date{" "}
            <span className="text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="purchaseDate"
            name="purchaseDate"
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            className={`w-full border rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.purchaseDate ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.purchaseDate && (
            <p className="text-red-500 text-sm mt-1" role="alert">
              {errors.purchaseDate}
            </p>
          )}
        </div>

        {/* New / Used */}
        <div className="mb-4">
          <span className="block text-base font-medium text-gray-700 mb-1">
            Condition
          </span>
          <div className="flex gap-4">
            <label className="checkbox-label">
              <input
                type="radio"
                name="condition"
                value="new"
                checked={isNew}
                onChange={() => setIsNew(true)}
              />
              New
            </label>
            <label className="checkbox-label">
              <input
                type="radio"
                name="condition"
                value="used"
                checked={!isNew}
                onChange={() => setIsNew(false)}
              />
              Used
            </label>
          </div>
        </div>

        {/* Receipt Upload */}
        <div className="mb-4">
          <label
            id="receiptImageLabel"
            className="block text-base font-medium text-gray-700 mb-1"
          >
            Receipt{" "}
            <span className="text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <div
            role="button"
            tabIndex={0}
            aria-labelledby="receiptImageLabel"
            aria-describedby="receiptImageError"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={handleDropZoneKeyDown}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDragEnd={() => setIsDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
              isDragOver
                ? "border-teal-500 bg-teal-50"
                : errors.receiptImage
                  ? "border-red-400 bg-red-50"
                  : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              id="receiptImage"
              name="receiptImage"
              type="file"
              accept="image/*,.pdf"
              aria-required="true"
              className="sr-only"
              onChange={handleFileChange}
            />
            {!file ? (
              <div className="flex flex-col items-center text-center text-gray-500 pointer-events-none select-none">
                <svg
                  className="w-10 h-10 mb-2 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 16V8m0 0-3 3m3-3 3 3M4 16.5A2.5 2.5 0 0 0 6.5 19h11a2.5 2.5 0 0 0 2.5-2.5V8.5L15 3H6.5A2.5 2.5 0 0 0 4 5.5v11z"
                  />
                </svg>
                <span className="text-base font-medium">
                  Click to upload or drag &amp; drop
                </span>
                <span className="text-sm mt-1 text-gray-400">
                  Image or PDF &nbsp;·&nbsp; Max 10 MB
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-teal-500 shrink-0 pointer-events-none"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16l4-4 4 4 4-6 4 6"
                  />
                </svg>
                <span className="text-base text-gray-700 truncate pointer-events-none">
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFile();
                  }}
                  aria-label="Remove selected file"
                  className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          {errors.receiptImage && (
            <p
              id="receiptImageError"
              className="text-red-500 text-sm mt-1"
              role="alert"
            >
              {errors.receiptImage}
            </p>
          )}
        </div>

        {/* Usage Location */}
        <div className="mb-4">
          <fieldset className="checkbox-group">
            <legend>
              Where is this product being used?{" "}
              <span style={{ fontWeight: 400, color: "#888" }}>(optional)</span>
            </legend>
            <div className="checkbox-options">
              {USAGE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="checkbox-label">
                  <input
                    type="checkbox"
                    name="usageLocation"
                    value={value}
                    checked={usageLocations.includes(value)}
                    onChange={() => toggleUsage(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {/* Email Opt-in */}
        <div className="mb-6">
          <label className="checkbox-label standalone">
            <input
              type="checkbox"
              name="emailOptIn"
              checked={emailOptIn}
              onChange={(e) => setEmailOptIn(e.target.checked)}
            />
            I'd like to receive emails about future products and promotions
          </label>
        </div>

        <div className="flex items-center justify-end gap-4 flex-wrap">
          {status && (
            <p
              className="text-base text-gray-700 m-0"
              role="status"
              aria-live="polite"
            >
              {status}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="cursor-pointer text-white bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium rounded-full text-base px-5 py-2.5 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-colors"
          >
            {submitting ? "Registering…" : "Register Product ›"}
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default ProductRegistrationForm;
